import {
  namedOperations,
  useAddInstagramCompareAccountAnalyticsMutation,
  useDeleteInstagramCompareAccountAnalyticsMutation
} from '@anytag/graphql';
import { useQueryHelper, useSelectItem } from '@anytag/shared-hooks';
import { AnalyticsInstagramComparableAccountType } from '../../types';

interface Props {
  mainAccountId: number;
}

export const useCreateDeleteInstagramComparableAccount = ({ mainAccountId }: Props) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const {
    selectedItem: deletingAccount,
    setSelectedItem: setDeletingAccount,
    resetSelectedItem: resetDeletingAccount
  } = useSelectItem<AnalyticsInstagramComparableAccountType | null>();
  const { callAddInstagramCompareAccountAnalytics, loading: adding } = useAddInstagramCompareAccountAnalyticsMutation({
    refetchQueries: [namedOperations.Query.InstagramCompareGetAccounts]
  });
  const { callDeleteInstagramCompareAccountAnalytics, loading: deleting } =
    useDeleteInstagramCompareAccountAnalyticsMutation({
      refetchQueries: [namedOperations.Query.InstagramCompareGetAccounts]
    });

  const handleAddNewComparableAccount = async (compareAccountIgUsername: string, callback?: () => void) => {
    if (!compareAccountIgUsername) return;

    try {
      await callAddInstagramCompareAccountAnalytics({
        variables: { input: { compareAccountIgUsername, mainAnalyticsAccountId: mainAccountId } }
      });

      callback?.();
      enqueueSnackbar(t('succeededInCreating', { name: compareAccountIgUsername }), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message || 'failedToCreate'), { variant: 'error' });
    }
  };

  const handleDeleteComparableAccount = async () => {
    if (deletingAccount) {
      try {
        await callDeleteInstagramCompareAccountAnalytics({
          variables: {
            input: { compareAccountId: deletingAccount.id, mainAnalyticsAccountId: mainAccountId }
          }
        });
        enqueueSnackbar(t('succeededInDeleting', { name: deletingAccount.username }), { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(t(error.message || 'failedToDelete'), { variant: 'error' });
      } finally {
        setDeletingAccount(null);
      }
    }
  };

  return {
    adding,
    deleting,
    deletingAccount,
    setDeletingAccount,
    resetDeletingAccount,
    handleAddNewComparableAccount,
    handleDeleteComparableAccount
  };
};
